import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CreditCard, X, Warning } from "@phosphor-icons/react";

// Räkna ut hela dagar kvar till slutet av prövoperioden. Vi räknar UP
// (Math.ceil) så en användare som har 5 minuter kvar av dag 30 fortfarande
// ser "1 dag kvar" — bättre än att slänga upp "0 dagar" prematurt.
function daysLeft(iso) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  const now = Date.now();
  const diff = end - now;
  if (diff <= 0) return 0;
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

const TOTAL_TRIAL_DAYS = 30;
const MODAL_STORAGE_KEY_PREFIX = "trial_modal_last_shown_v1_";

/** Bestäm bannerlevel utifrån dagar kvar.
 *  green = "gott om tid" (dag 15+ kvar), yellow = "börjar bli dags" (7-14),
 *  red = "sista veckan" (1-6), critical = "sista dagen".
 *  Post-trial (dag 30+) hanteras separat via read-only-läge från backend. */
function trialLevel(remaining) {
  if (remaining <= 1) return "critical";
  if (remaining <= 6) return "red";
  if (remaining <= 14) return "yellow";
  return "green";
}

const LEVEL_STYLES = {
  green:    "bg-emerald-500/10 border-emerald-500/30 text-emerald-100",
  yellow:   "bg-amber-500/10  border-amber-500/30  text-amber-100",
  red:      "bg-red-500/15    border-red-500/40    text-red-100",
  critical: "bg-red-600/25    border-red-500/60    text-white",
};

const LEVEL_BAR_COLOR = {
  green:    "bg-emerald-400",
  yellow:   "bg-amber-400",
  red:      "bg-red-400",
  critical: "bg-red-300",
};

// Bannern renderas endast för organisationer med status `trialing`
// eller (för read-only-läget) `trial_expired`. Aktiva/grandfathered
// orgar ska INTE se den.
export default function TrialBanner() {
  const { subscription, user } = useAuth();
  const [showModal, setShowModal] = useState(false);

  const status = subscription?.subscription_status;
  const remaining = daysLeft(subscription?.trial_ends_at);
  const isTrialing = status === "trialing";
  const isExpired = status === "trial_expired";
  const level = trialLevel(remaining ?? 0);

  // Modal från dag 25+ (dvs remaining <= 6): en gång per admin per dag,
  // baserat på localStorage. Anställda ser bara bannern.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    if (isExpired) {
      setShowModal(true);
      return;
    }
    if (!isTrialing || remaining === null) return;
    if (remaining > 5) return; // Endast dag 25+ (5 dagar eller mindre)
    const key = MODAL_STORAGE_KEY_PREFIX + user.user_id;
    const lastShown = localStorage.getItem(key);
    const today = new Date().toISOString().slice(0, 10);
    if (lastShown !== today) {
      setShowModal(true);
    }
  }, [user, isTrialing, isExpired, remaining]);

  const dismissModal = () => {
    setShowModal(false);
    if (user && !isExpired) {
      const key = MODAL_STORAGE_KEY_PREFIX + user.user_id;
      localStorage.setItem(key, new Date().toISOString().slice(0, 10));
    }
  };

  // --- Read-only mode (trial expired) ---
  if (isExpired) {
    return (
      <>
        <div
          data-testid="trial-banner-expired"
          className="border-b bg-red-600 border-red-700 text-white"
        >
          <div className="px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
            <Warning size={20} weight="fill" className="flex-shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              <strong>Din prövotid har löpt ut.</strong> Du kan se historik men inte skapa nya rapporter förrän du aktiverar abonnemanget.
            </div>
            <Link
              to="/billing"
              data-testid="trial-banner-cta"
              className="inline-flex items-center gap-2 bg-white text-red-700 hover:bg-red-50 rounded-md px-4 py-2 text-sm font-medium whitespace-nowrap"
            >
              <CreditCard size={16} /> Aktivera abonnemang
            </Link>
          </div>
        </div>
        {showModal && user?.role === "admin" && (
          <TrialExpiredModal onClose={dismissModal} />
        )}
      </>
    );
  }

  // --- Trial ongoing ---
  if (!isTrialing || remaining === null) return null;

  const elapsed = Math.max(0, TOTAL_TRIAL_DAYS - remaining);
  const pct = Math.min(100, Math.round((elapsed / TOTAL_TRIAL_DAYS) * 100));

  return (
    <>
      <div
        data-testid="trial-banner"
        data-trial-level={level}
        className={"border-b " + LEVEL_STYLES[level]}
      >
        <div className="px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium flex items-center gap-2">
              {level === "critical" && <Warning size={16} weight="fill" />}
              {remaining === 0
                ? "Sista dagen av din prövotid"
                : remaining === 1
                  ? "1 dag kvar av din prövotid"
                  : `${remaining} dagar kvar av din prövotid`}
            </div>
            <div className="mt-2 h-1 w-full max-w-md bg-white/10 rounded-full overflow-hidden">
              <div
                className={"h-full transition-all duration-500 " + LEVEL_BAR_COLOR[level]}
                style={{ width: `${pct}%` }}
                data-testid="trial-banner-progress"
              />
            </div>
          </div>
          <Link
            to="/billing"
            data-testid="trial-banner-cta"
            className={
              "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium whitespace-nowrap " +
              (level === "critical" || level === "red"
                ? "bg-white text-red-700 hover:bg-red-50"
                : "bg-white/15 hover:bg-white/25 text-white border border-white/20")
            }
          >
            <CreditCard size={16} /> Aktivera nu
          </Link>
        </div>
      </div>
      {showModal && user?.role === "admin" && (
        <TrialModal remaining={remaining} onClose={dismissModal} />
      )}
    </>
  );
}

function TrialModal({ remaining, onClose }) {
  return (
    <div
      data-testid="trial-modal"
      className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-app rounded-2xl p-6 w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-text-muted hover:text-text-primary"
          data-testid="trial-modal-close"
          aria-label="Stäng"
        >
          <X size={20} />
        </button>
        <div className="flex items-center gap-2 mb-3">
          <Warning size={22} className="text-red-600" />
          <h2 className="font-heading text-xl">
            {remaining <= 1
              ? "Din prövotid tar slut nu"
              : `${remaining} dagar kvar av din prövotid`}
          </h2>
        </div>
        <p className="text-sm text-text-muted mb-4 leading-relaxed">
          Aktivera abonnemanget nu för att inte förlora tillgång till dina rapporter, moment och anställda. All data behålls oförändrad.
        </p>
        <ul className="text-sm text-text-primary space-y-2 mb-6">
          <li>· Fortsätt stämpla och exportera PDF som vanligt</li>
          <li>· Personalliggare håller sig laglig</li>
          <li>· Avsluta när som helst från kontosidan</li>
        </ul>
        <div className="flex gap-2">
          <button onClick={onClose} className="tk-btn-outline flex-1">
            Påminn mig imorgon
          </button>
          <Link
            to="/billing"
            onClick={onClose}
            data-testid="trial-modal-cta"
            className="tk-btn-primary flex-1 inline-flex items-center justify-center gap-2"
          >
            <CreditCard size={16} /> Aktivera
          </Link>
        </div>
      </div>
    </div>
  );
}

function TrialExpiredModal({ onClose }) {
  return (
    <div
      data-testid="trial-expired-modal"
      className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4"
    >
      <div className="bg-surface border border-red-500/40 rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center gap-2 mb-3">
          <Warning size={22} weight="fill" className="text-red-600" />
          <h2 className="font-heading text-xl">Prövotiden har löpt ut</h2>
        </div>
        <p className="text-sm text-text-muted mb-4 leading-relaxed">
          Du kan se din historik och exportera gamla rapporter, men inte skapa nya stämplingar eller ändra data förrän du aktiverar abonnemanget.
        </p>
        <div className="flex gap-2">
          <button onClick={onClose} className="tk-btn-outline flex-1">
            Fortsätt läsvy
          </button>
          <Link
            to="/billing"
            onClick={onClose}
            data-testid="trial-expired-modal-cta"
            className="tk-btn-primary flex-1 inline-flex items-center justify-center gap-2"
          >
            <CreditCard size={16} /> Aktivera nu
          </Link>
        </div>
      </div>
    </div>
  );
}
